/**
 * Envelope encryption for published blobs.
 *
 * Isomorphic WebCrypto: the pusher encrypts under Node, the browser decrypts,
 * and the storage in between never holds a key. That is the whole point of the
 * design — Cloudflare stores bytes it cannot read, so "can the host see my work
 * usage" has a structural answer rather than a policy one.
 *
 * The Worker deliberately has no decrypt path. If it could read a blob, the
 * zero-knowledge property would depend on the Worker's code staying honest
 * across every future edit, which is a much weaker guarantee than it not having
 * the key at all.
 */

const MAGIC = new Uint8Array([0x53, 0x54, 0x41, 0x54, 0x53, 0x41, 0x49]); // "STATSAI"
const SCHEME_VERSION = 1;
const IV_BYTES = 12; // AES-GCM standard nonce length
const KEY_BITS = 256;

/**
 * PBKDF2 work factor. OWASP's floor for PBKDF2-HMAC-SHA256 is 600k, and there is
 * no reason to sit at the floor: this runs a handful of times per page load, not
 * per request.
 */
const PBKDF2_ITERATIONS = 600_000;

/**
 * A fixed derivation salt, which is the right call *here* specifically.
 *
 * A salt's job is stopping one precomputed table from being reused across many
 * users. There is one user, one self-chosen passphrase, and the ciphertext is
 * not part of a breached password corpus — so a per-user random salt buys close
 * to nothing, while a per-*message* random salt would actively hurt: every blob
 * would need its own 600k-iteration derivation on page load.
 *
 * The security here rests on passphrase strength against 600k iterations, which
 * is why {@link MIN_PASSPHRASE_LENGTH} is enforced rather than suggested. Treat
 * this constant as public — it is in the repo, and the design assumes as much.
 */
const SALT = new TextEncoder().encode("statsai/blob/v1");

/**
 * Short passphrases are the only realistic break in this scheme, so they are
 * refused outright instead of warned about. A rejected setup is recoverable; a
 * year of blobs encrypted under "hunter2" is not.
 */
export const MIN_PASSPHRASE_LENGTH = 12;

export class CryptoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CryptoError";
  }
}

/** In-memory cache so a passphrase is stretched once per process/page, not per blob. */
const keyCache = new Map<string, Promise<CryptoKey>>();

/**
 * Stretch a passphrase into the AES key. Cached by passphrase, since the salt
 * and parameters are fixed and derivation is deliberately expensive.
 */
export function deriveKey(passphrase: string): Promise<CryptoKey> {
  if (passphrase.length < MIN_PASSPHRASE_LENGTH) {
    throw new CryptoError(
      `passphrase must be at least ${MIN_PASSPHRASE_LENGTH} characters`,
    );
  }
  const cached = keyCache.get(passphrase);
  if (cached) return cached;

  const derived = (async () => {
    const material = await crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(passphrase),
      "PBKDF2",
      false,
      ["deriveKey"],
    );
    return crypto.subtle.deriveKey(
      { name: "PBKDF2", salt: SALT as BufferSource, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
      material,
      { name: "AES-GCM", length: KEY_BITS },
      false,
      ["encrypt", "decrypt"],
    );
  })();

  keyCache.set(passphrase, derived);
  return derived;
}

/**
 * Encrypt payload bytes into a self-describing envelope:
 *
 *   "STATSAI" | version (1 byte) | IV (12 bytes) | ciphertext+GCM tag
 *
 * The magic prefix means a truncated, corrupted, or simply unrelated value is
 * rejected with a clear message instead of surfacing as a decryption failure
 * that reads like a wrong passphrase. Telling those two apart matters when the
 * user is staring at a dashboard wondering which one they broke.
 *
 * A fresh random IV per call is what keeps a fixed key safe — reusing a nonce
 * under AES-GCM is catastrophic, so it is generated here and never passed in.
 */
export async function encryptBlob(
  payload: Uint8Array,
  passphrase: string,
): Promise<Uint8Array> {
  const key = await deriveKey(passphrase);
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv as BufferSource },
      key,
      payload as BufferSource,
    ),
  );

  const out = new Uint8Array(MAGIC.length + 1 + IV_BYTES + ciphertext.length);
  out.set(MAGIC, 0);
  out[MAGIC.length] = SCHEME_VERSION;
  out.set(iv, MAGIC.length + 1);
  out.set(ciphertext, MAGIC.length + 1 + IV_BYTES);
  return out;
}

/** Reverse {@link encryptBlob}, distinguishing "not our data" from "wrong key". */
export async function decryptBlob(
  envelope: Uint8Array,
  passphrase: string,
): Promise<Uint8Array> {
  const header = MAGIC.length + 1 + IV_BYTES;
  if (envelope.length <= header) {
    throw new CryptoError("encrypted blob is truncated");
  }
  for (let i = 0; i < MAGIC.length; i++) {
    if (envelope[i] !== MAGIC[i]) {
      throw new CryptoError("not a StatsAI encrypted blob");
    }
  }
  const version = envelope[MAGIC.length];
  if (version !== SCHEME_VERSION) {
    throw new CryptoError(
      `unsupported encryption scheme v${version} (this build reads v${SCHEME_VERSION})`,
    );
  }

  const key = await deriveKey(passphrase);
  const iv = envelope.subarray(MAGIC.length + 1, header);
  const ciphertext = envelope.subarray(header);
  try {
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv as BufferSource },
      key,
      ciphertext as BufferSource,
    );
    return new Uint8Array(plain);
  } catch {
    // GCM authentication covers both a wrong key and tampered bytes; the API
    // cannot tell them apart, so neither do we rather than guessing.
    throw new CryptoError(
      "could not decrypt — wrong passphrase, or the stored data was altered",
    );
  }
}

/** Base64 for transport, since the ciphertext travels as JSON and rests in a TEXT column. */
export function toBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000; // chunked so a large blob can't blow the argument limit
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
